import React from "react";
import { ChevronRight } from "lucide-react";
import { fmtInr, fmtPct, pnlClass } from "../lib/format";

function StatCard({ label, value, sub, valueClass = "", onClick, testId, accent }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`text-left card-noir p-5 rounded-lg block w-full group ${
        onClick ? "cursor-pointer hover:-translate-y-[1px]" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-foreground tracking-[0.24em] uppercase font-mono font-semibold">{label}</div>
        {onClick ? (
          <ChevronRight
            className="h-3.5 w-3.5 text-muted-foreground/70 group-hover:text-lime transition-colors"
            strokeWidth={1.5}
          />
        ) : null}
      </div>
      <div className={`font-display text-2xl sm:text-3xl font-bold mt-2 tabular tracking-tight ${valueClass}`}>{value}</div>
      {sub ? <div className="text-[11px] sm:text-xs text-muted-foreground mt-1.5 line-clamp-2">{sub}</div> : null}
      {accent ? <div className="mt-3 h-[2px] w-8 bg-lime" /> : null}
    </Tag>
  );
}

export default function OverviewSection({ data, perf, onJump }) {
  const t = data.totals || {};
  const totalPnl = (t.unrealized_pnl || 0) + (data.realized_pnl || 0);
  const cashPct = t.total_capital > 0 ? (t.cash_balance / t.total_capital) * 100 : 0;
  const deployedPct = t.deployment_pct || 0;
  // Canonical portfolio return: (NAV - Capital Invested) / Capital Invested.
  // Backend already provides this as t.overall_return_pct; falling back to local calc.
  const totalReturnPct = t.overall_return_pct != null
    ? t.overall_return_pct
    : ((t.portfolio_nav || 0) / (t.total_capital || 1) - 1) * 100;

  return (
    <div className="space-y-6">
      {/* Hero block */}
      <div className="card-noir p-5 sm:p-7 rounded-lg relative overflow-hidden" data-testid="capital-deployment">
        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, hsl(var(--lime) / 0.15) 0%, transparent 65%)" }} />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-6">
          <div>
            <div className="text-[10px] text-muted-foreground tracking-[0.28em] uppercase font-mono font-semibold">
              Portfolio NAV
            </div>
            <div className="font-display text-5xl sm:text-6xl font-extrabold tracking-tighter mt-2 tabular">
              <span className="text-muted-foreground text-3xl sm:text-4xl align-top mr-1">₹</span>
              {Number(t.portfolio_nav || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </div>
            <div className={`text-sm mt-2 font-mono font-semibold ${pnlClass(totalPnl)}`}>
              {totalPnl >= 0 ? "▲" : "▼"} {fmtInr(Math.abs(totalPnl))} ({fmtPct(totalReturnPct)})
              <span className="text-muted-foreground font-normal ml-2">all-time</span>
            </div>
            {t.day_pnl != null && (t.day_pnl !== 0 || t.day_change_pct !== 0) ? (
              <div
                data-testid="overview-day-pnl"
                className={`text-xs mt-1 font-mono font-semibold ${pnlClass(t.day_pnl)}`}
              >
                {t.day_pnl >= 0 ? "▲" : "▼"} {fmtInr(Math.abs(t.day_pnl))} ({fmtPct(t.day_change_pct)})
                <span className="text-muted-foreground font-normal ml-2">today</span>
              </div>
            ) : null}
          </div>
          <div className="lg:text-right">
            <div className="text-[10px] text-muted-foreground tracking-[0.28em] uppercase font-mono font-semibold">
              Capital invested
            </div>
            <div className="font-display text-2xl sm:text-3xl font-bold tabular mt-2">
              {fmtInr(t.total_capital)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1 font-mono">
              Initial {fmtInr((t.total_capital || 0) - (t.additions_total || 0))}
              {t.additions_total > 0 ? <> · Additions {fmtInr(t.additions_total)}</> : null}
            </div>
          </div>
        </div>

        {/* Allocation bar */}
        <div className="relative space-y-2.5" data-testid="allocation-bar">
          <div className="h-2 w-full bg-secondary border border-border flex overflow-hidden rounded-sm">
            <div
              className="h-full bg-saffron"
              style={{ width: `${Math.max(0, Math.min(100, deployedPct))}%` }}
              title={`Deployed ${deployedPct.toFixed(1)}%`}
            />
            <div
              className="h-full bg-navy"
              style={{ width: `${Math.max(0, Math.min(100, cashPct))}%` }}
              title={`Cash ${cashPct.toFixed(1)}%`}
            />
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-3 text-[11px] text-muted-foreground font-mono">
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              <span className="h-2 w-2 bg-saffron inline-block" />
              <span className="uppercase tracking-[0.18em]">Deployed</span>
              <span className="text-foreground">
                {fmtInr(t.deployed_capital)} · {deployedPct.toFixed(1)}%
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              <span className="h-2 w-2 bg-navy inline-block" />
              <span className="uppercase tracking-[0.18em]">Cash</span>
              <span className="text-foreground">
                {fmtInr(t.cash_balance)} · {cashPct.toFixed(1)}%
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Deployed"
          value={fmtInr(t.deployed_capital)}
          sub={`${data.holdings.length} positions · ${deployedPct.toFixed(1)}%`}
          onClick={() => onJump?.("holdings")}
          testId="overview-deployed"
        />
        <StatCard
          label="Market Value"
          value={fmtInr(t.market_value)}
          sub={`Unrealized ${fmtInr(t.unrealized_pnl)}`}
          valueClass={pnlClass(t.unrealized_pnl)}
          onClick={() => onJump?.("holdings")}
          testId="overview-mv"
        />
        <StatCard
          label="Cash"
          value={fmtInr(t.cash_balance)}
          sub={`${cashPct.toFixed(1)}% of capital`}
          valueClass="text-lime"
          testId="overview-cash"
        />
        <StatCard
          label="Total Return"
          value={fmtInr(totalPnl)}
          sub={`${fmtPct(totalReturnPct)} on capital invested`}
          valueClass={pnlClass(totalPnl)}
          onClick={() => onJump?.("performance")}
          testId="overview-pnl"
        />
      </div>

      {/* Performance snapshot */}
      {perf?.summary ? (
        <div>
          <div className="text-[10px] text-muted-foreground tracking-[0.28em] uppercase font-mono font-semibold mb-3">
            vs Benchmarks <span className="text-muted-foreground/70 normal-case tracking-normal font-sans">— return on capital invested</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["Portfolio", totalReturnPct, true],
              ["NIFTY 50", perf.summary.nifty50_return, false],
              ["SENSEX", perf.summary.sensex_return, false],
              ["NIFTY 500", perf.summary.nifty500_return, false],
            ].map(([label, val, primary]) => (
              <button
                key={label}
                onClick={() => onJump?.("performance")}
                className={`card-noir p-4 text-left rounded-lg hover:-translate-y-[1px] ${primary ? "border-lime/40" : ""}`}
              >
                <div className="text-[10px] text-muted-foreground tracking-[0.24em] uppercase font-mono">{label}</div>
                <div className={`font-display text-2xl font-bold mt-1 tabular tracking-tight ${pnlClass(val)}`}>
                  {fmtPct(val)}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
